import * as React from 'react'
import { createFileRoute } from '@tanstack/react-router'
import { motion } from 'framer-motion'
import {
  TrendingUp,
  TrendingDown,
  Users,
  Stethoscope,
  Receipt,
  Download,
  CalendarIcon,
  Loader2,
  ChevronDown,
  UserPlus,
  UserX,
  Activity,
  Crown,
  AlertTriangle,
  Phone,
  FileText,
  Building2,
  Target,
  FileSpreadsheet,
  Printer,
  CheckCircle2,
  Clock,
  ArrowRight,
} from 'lucide-react'
import { format } from 'date-fns'
import type { DateRange } from 'react-day-picker'
import {
  LineChart,
  Line,
  BarChart,
  Bar,
  AreaChart,
  Area,
  PieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Legend,
  Brush,
} from 'recharts'

import { Header } from '@/components/layout/header'
import { Button } from '@/components/ui/button'
import { Calendar } from '@/components/ui/calendar'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs'
import { cn } from '@/lib/utils'
import {
  exportRevenueToCSV,
  exportDataToCSV,
  printReportAsPDF,
  generateHTMLTable,
  generateSummaryCardsHTML,
  formatCurrencyForExport,
} from '@/lib/export'
import { useAnalyticsOverview, useRevenueAnalytics, usePatientAnalytics, useTreatmentAnalytics, useReportTemplates, useGenerateReport } from '@/hooks'
import { useBranch } from '@/providers/branch-provider'
import type {
  KpiMetric,
  RevenueTrendItem,
  BranchComparisonItem,
  PatientTypeItem,
  TopTreatmentItem,
  RevenueTrendDataItem,
  RevenueBranchItem,
  RevenueCategoryItem,
  MonthlyComparisonItem,
  AcquisitionTrendItem,
  VisitFrequencyItem,
  PatientsByBranchItem,
  TopPatientItem,
  InactivePatientItem,
  PopularTreatmentItem,
  CategoryBreakdownItem,
  TreatmentTrendItem,
  TreatmentTypeItem,
  TreatmentsByBranchItem,
  CrossSellItem,
  ReportTemplate,
  ReportTemplateId,
  GeneratedReport,
  MonthlyRevenueReportData,
  PatientAnalyticsReportData,
  TreatmentPerformanceReportData,
  BranchComparisonReportData,
  FinancialSummaryReportData,
  MarketingInsightsReportData,
} from '@/lib/api-client'

export const Route = createFileRoute('/analytics')({
  component: AnalyticsPage,
})

// ============================================================================
// Main Page Component
// ============================================================================
type TabId = 'overview' | 'revenue' | 'patients' | 'treatments' | 'reports'

function AnalyticsPage() {
  const { branchId, selectedBranch } = useBranch()
  const [activeTab, setActiveTab] = React.useState<TabId>('overview')
  const [dateRange, setDateRange] = React.useState<DateRange | undefined>({
    from: new Date(2025, 0, 1), // Jan 1, 2025
    to: new Date(2025, 11, 31), // Dec 31, 2025
  })

  // Fetch analytics overview - pass date range to API
  const { data, isPending, isError } = useAnalyticsOverview({
    branchId,
    startDate: dateRange?.from ? format(dateRange.from, 'yyyy-MM-dd') : undefined,
    endDate: dateRange?.to ? format(dateRange.to, 'yyyy-MM-dd') : undefined,
    compareWithPrevious: true,
  })

  // Fetch revenue analytics for Revenue tab
  const [groupBy, setGroupBy] = React.useState<'day' | 'week' | 'month'>('day')
  const {
    data: revenueData,
    isPending: revenuePending,
    isError: revenueError,
  } = useRevenueAnalytics({
    branchId,
    startDate: dateRange?.from ? format(dateRange.from, 'yyyy-MM-dd') : undefined,
    endDate: dateRange?.to ? format(dateRange.to, 'yyyy-MM-dd') : undefined,
    groupBy,
  })

  // Fetch patient analytics for Patients tab
  const {
    data: patientData,
    isPending: patientPending,
    isError: patientError,
  } = usePatientAnalytics({
    branchId,
    startDate: dateRange?.from ? format(dateRange.from, 'yyyy-MM-dd') : undefined,
    endDate: dateRange?.to ? format(dateRange.to, 'yyyy-MM-dd') : undefined,
  })

  // Fetch treatment analytics for Treatments tab
  const {
    data: treatmentData,
    isPending: treatmentPending,
    isError: treatmentError,
  } = useTreatmentAnalytics({
    branchId,
    startDate: dateRange?.from ? format(dateRange.from, 'yyyy-MM-dd') : undefined,
    endDate: dateRange?.to ? format(dateRange.to, 'yyyy-MM-dd') : undefined,
  })

  return (
    <div className="flex flex-col gap-4 sm:gap-7">
      {/* Header */}
      <Header
        title="Analytics"
        subtitle={
          data?.period
            ? `${formatDate(data.period.startDate)} - ${formatDate(data.period.endDate)} | ${selectedBranch.name}`
            : 'Loading...'
        }
      >
        <div className="flex items-center gap-2">
          <Popover>
            <PopoverTrigger asChild>
              <Button variant="outline" size="sm" className="gap-2 font-normal">
                <CalendarIcon className="w-4 h-4" />
                {dateRange?.from ? (
                  dateRange.to ? (
                    <span className="hidden sm:inline">
                      {format(dateRange.from, 'dd MMM')} - {format(dateRange.to, 'dd MMM yyyy')}
                    </span>
                  ) : (
                    <span className="hidden sm:inline">{format(dateRange.from, 'dd MMM yyyy')}</span>
                  )
                ) : (
                  <span className="hidden sm:inline">Date Range</span>
                )}
                <ChevronDown className="w-3 h-3 opacity-50" />
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-auto p-0" align="end">
              <Calendar
                mode="range"
                defaultMonth={dateRange?.from}
                selected={dateRange}
                onSelect={setDateRange}
                numberOfMonths={2}
              />
            </PopoverContent>
          </Popover>
          <Button
            variant="outline"
            size="sm"
            className="gap-2"
            onClick={() => {
              if (activeTab === 'revenue' && revenueData?.monthlyComparison) {
                exportRevenueToCSV(revenueData.monthlyComparison)
              }
            }}
            disabled={activeTab !== 'revenue' || !revenueData?.monthlyComparison}
          >
            <Download className="w-4 h-4" />
            <span className="hidden sm:inline">Export</span>
          </Button>
        </div>
      </Header>

      {/* Tabs Navigation */}
      <div className="px-4 sm:px-6 lg:px-9">
        <Tabs value={activeTab} onValueChange={(v) => setActiveTab(v as TabId)}>
          <TabsList>
            <TabsTrigger value="overview">Overview</TabsTrigger>
            <TabsTrigger value="revenue">Revenue</TabsTrigger>
            <TabsTrigger value="patients">Patients</TabsTrigger>
            <TabsTrigger value="treatments">Treatments</TabsTrigger>
            <TabsTrigger value="reports">Reports</TabsTrigger>
          </TabsList>

          {/* Tab Content */}
          <div className="mt-5 lg:mt-7">
            {/* Overview Tab */}
            <TabsContent value="overview" className="mt-0">
              <OverviewTabContent
                data={data}
                isPending={isPending}
                isError={isError}
              />
            </TabsContent>

            {/* Revenue Tab */}
            <TabsContent value="revenue" className="mt-0">
              <RevenueTabContent
                data={revenueData}
                isPending={revenuePending}
                isError={revenueError}
                groupBy={groupBy}
                onGroupByChange={setGroupBy}
              />
            </TabsContent>

            {/* Patients Tab */}
            <TabsContent value="patients" className="mt-0">
              <PatientTabContent
                data={patientData}
                isPending={patientPending}
                isError={patientError}
              />
            </TabsContent>

            {/* Treatments Tab */}
            <TabsContent value="treatments" className="mt-0">
              <TreatmentTabContent
                data={treatmentData}
                isPending={treatmentPending}
                isError={treatmentError}
              />
            </TabsContent>

            {/* Reports Tab */}
            <TabsContent value="reports" className="mt-0">
              <ReportTabContent
                dateRange={dateRange}
                branchId={branchId}
                branchName={selectedBranch.name}
              />
            </TabsContent>
          </div>
        </Tabs>
      </div>
    </div>
  )
}

// ============================================================================
// Overview Tab Content
// ============================================================================
interface OverviewTabContentProps {
  data: ReturnType<typeof useAnalyticsOverview>['data']
  isPending: boolean
  isError: boolean
}

function OverviewTabContent({ data, isPending, isError }: OverviewTabContentProps) {
  if (isPending) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    )
  }

  if (isError) {
    return (
      <div className="text-center py-20 text-destructive">
        Failed to load analytics data. Please try again.
      </div>
    )
  }

  if (!data) return null

  return (
    <div className="flex flex-col gap-5 lg:gap-7 pb-6 lg:pb-9">
      {/* KPI Cards Grid */}
      <div className="grid gap-3 sm:gap-4.5 grid-cols-2 lg:grid-cols-4">
        <KpiCard
          title="Revenue"
          value={formatCurrency(data.kpis.revenue.value)}
          growth={data.kpis.revenue.growth}
          icon={TrendingUp}
          delay={0}
        />
        <KpiCard
          title="Patients"
          value={formatNumber(data.kpis.patients.value)}
          growth={data.kpis.patients.growth}
          icon={Users}
          delay={0.1}
        />
        <KpiCard
          title="Treatments"
          value={formatNumber(data.kpis.treatments.value)}
          growth={data.kpis.treatments.growth}
          icon={Stethoscope}
          delay={0.2}
        />
        <KpiCard
          title="Avg Transaction"
          value={formatCurrency(data.kpis.avgTransaction.value)}
          growth={data.kpis.avgTransaction.growth}
          icon={Receipt}
          delay={0.3}
        />
      </div>

      {/* Charts Row 1 */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 lg:gap-6">
        <RevenueTrendChart data={data.charts.revenueTrend} />
        <BranchComparisonChart data={data.charts.branchComparison} />
      </div>

      {/* Charts Row 2 */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 lg:gap-6">
        <PatientTypeChart data={data.charts.patientTypes} />
        <TopTreatmentsChart data={data.charts.topTreatments} />
      </div>
    </div>
  )
}

// ============================================================================
// Revenue Tab Content
// ============================================================================
interface RevenueTabContentProps {
  data: ReturnType<typeof useRevenueAnalytics>['data']
  isPending: boolean
  isError: boolean
  groupBy: 'day' | 'week' | 'month'
  onGroupByChange: (value: 'day' | 'week' | 'month') => void
}

function RevenueTabContent({ data, isPending, isError, groupBy, onGroupByChange }: RevenueTabContentProps) {
  if (isPending) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    )
  }

  if (isError) {
    return (
      <div className="text-center py-20 text-destructive">
        Failed to load revenue data. Please try again.
      </div>
    )
  }

  if (!data) return null

  return (
    <div className="flex flex-col gap-5 lg:gap-7 pb-6 lg:pb-9">
      {/* Summary Cards */}
      <div className="grid gap-3 sm:gap-4.5 grid-cols-2 lg:grid-cols-4">
        <RevenueSummaryCard
          title="Total Revenue"
          value={formatCurrency(data.summary.totalRevenue)}
          delay={0}
        />
        <RevenueSummaryCard
          title="Total Invoices"
          value={formatNumber(data.summary.totalInvoices)}
          delay={0.1}
        />
        <RevenueSummaryCard
          title="Avg Daily"
          value={formatCurrency(data.summary.avgDaily)}
          delay={0.2}
        />
        <RevenueSummaryCard
          title="Avg Transaction"
          value={formatCurrency(data.summary.avgTransaction)}
          delay={0.3}
        />
      </div>

      {/* Revenue Trend Chart with Brush */}
      <DailyRevenueTrendChart
        data={data.charts.revenueTrend}
        groupBy={groupBy}
        onGroupByChange={onGroupByChange}
      />

      {/* Charts Row */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 lg:gap-6">
        <RevenueByBranchChart data={data.charts.byBranch} />
        <RevenueByCategoryChart data={data.charts.byCategory} />
      </div>

      {/* Monthly Comparison Table */}
      <MonthlyComparisonTable data={data.monthlyComparison} />
    </div>
  )
}

// ============================================================================
// Revenue Summary Card
// ============================================================================
interface RevenueSummaryCardProps {
  title: string
  value: string
  delay?: number
}

function RevenueSummaryCard({ title, value, delay = 0 }: RevenueSummaryCardProps) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay, duration: 0.3 }}
      className="bg-card rounded-xl p-4 sm:p-5"
    >
      <div className="text-xl sm:text-2xl font-bold text-text-dark mb-1">{value}</div>
      <div className="text-xs sm:text-sm text-text-muted">{title}</div>
    </motion.div>
  )
}

// ============================================================================
// Daily Revenue Trend Chart (Area with Brush)
// ============================================================================
interface DailyRevenueTrendChartProps {
  data: RevenueTrendDataItem[]
  groupBy: 'day' | 'week' | 'month'
  onGroupByChange: (value: 'day' | 'week' | 'month') => void
}

function DailyRevenueTrendChart({ data, groupBy, onGroupByChange }: DailyRevenueTrendChartProps) {
  const formatYAxis = (value: number) => {
    if (value >= 1000000) return `${(value / 1000000).toFixed(1)}M`
    if (value >= 1000) return `${(value / 1000).toFixed(0)}K`
    return value.toString()
  }

  const CustomTooltip = ({ active, payload, label }: any) => {
    if (active && payload && payload.length) {
      const item = payload[0].payload as RevenueTrendDataItem
      return (
        <div className="bg-card border border-border rounded-lg px-3 py-2 shadow-lg">
          <p className="text-sm font-medium text-foreground">{item.label}</p>
          <p className="text-sm text-emerald-500 font-semibold">
            {formatCurrency(item.revenue)}
          </p>
          <p className="text-xs text-text-muted">
            {item.invoices} invoices | {item.patients} patients
          </p>
        </div>
      )
    }
    return null
  }

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: 0.4, duration: 0.3 }}
      className="bg-card rounded-xl p-4 sm:p-6"
    >
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-sm sm:text-[15px] font-semibold text-text-dark">Revenue Trend</h3>
        <div className="flex items-center gap-1 bg-muted rounded-lg p-1">
          {(['day', 'week', 'month'] as const).map((option) => (
            <button
              key={option}
              onClick={() => onGroupByChange(option)}
              className={cn(
                'px-3 py-1 text-xs font-medium rounded-md transition-colors',
                groupBy === option
                  ? 'bg-icon-beige text-foreground'
                  : 'text-text-muted hover:text-foreground'
              )}
            >
              {option.charAt(0).toUpperCase() + option.slice(1)}
            </button>
          ))}
        </div>
      </div>
      <div className="h-[320px]">
        <ResponsiveContainer width="100%" height="100%">
          <AreaChart data={data} margin={{ top: 10, right: 10, left: -10, bottom: 0 }}>
            <defs>
              <linearGradient id="revenueGradient" x1="0" y1="0" x2="0" y2="1">
                <stop offset="5%" stopColor="#10b981" stopOpacity={0.3} />
                <stop offset="95%" stopColor="#10b981" stopOpacity={0} />
              </linearGradient>
            </defs>
            <CartesianGrid strokeDasharray="3 3" stroke="var(--color-border)" />
            <XAxis
              dataKey="label"
              tick={{ fontSize: 10, fill: 'var(--color-text-muted)' }}
              tickLine={false}
              axisLine={false}
              interval="preserveStartEnd"
            />
            <YAxis
              tickFormatter={formatYAxis}
              tick={{ fontSize: 11, fill: 'var(--color-text-muted)' }}
              tickLine={false}
              axisLine={false}
              width={50}
            />
            <Tooltip content={<CustomTooltip />} />
            <Area
              type="monotone"
              dataKey="revenue"
              stroke="#10b981"
              strokeWidth={2}
              fill="url(#revenueGradient)"
            />
            {data.length > 30 && (
              <Brush
                dataKey="label"
                height={30}
                stroke="#10b981"
                fill="var(--color-muted)"
                travellerWidth={10}
              />
            )}
          </AreaChart>
        </ResponsiveContainer>
      </div>
    </motion.div>
  )
}

// ============================================================================
// Revenue by Branch Chart (Horizontal Bar)
// ============================================================================
interface RevenueByBranchChartProps {
  data: RevenueBranchItem[]
}

function RevenueByBranchChart({ data }: RevenueByBranchChartProps) {
  const formatYAxis = (value: number) => {
    if (value >= 1000000) return `${(value / 1000000).toFixed(1)}M`
    if (value >= 1000) return `${(value / 1000).toFixed(0)}K`
    return value.toString()
  }

  const CustomTooltip = ({ active, payload }: any) => {
    if (active && payload && payload.length) {
      const branch = payload[0].payload as RevenueBranchItem
      return (
        <div className="bg-card border border-border rounded-lg px-3 py-2 shadow-lg">
          <p className="text-sm font-medium text-foreground">{branch.name}</p>
          <p className="text-sm text-primary font-semibold">
            {formatCurrency(branch.revenue)}
          </p>
          <p className="text-xs text-text-muted">
            {branch.percentage}% | {branch.invoices.toLocaleString()} invoices
          </p>
        </div>
      )
    }
    return null
  }

  // Add short names for display
  const chartData = data.map((b) => ({
    ...b,
    shortName: BRANCH_SHORT_NAMES[b.code] || b.code.toUpperCase(),
  }))

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: 0.5, duration: 0.3 }}
      className="bg-card rounded-xl p-4 sm:p-6"
    >
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-sm sm:text-[15px] font-semibold text-text-dark">Revenue by Branch</h3>
      </div>
      {/* Legend */}
      <div className="flex flex-wrap gap-3 sm:gap-4 mb-4">
        {chartData.map((branch) => (
          <div key={branch.code} className="flex items-center gap-2 text-xs text-text-muted">
            <span
              className="w-2.5 h-2.5 rounded-full"
              style={{ backgroundColor: BRANCH_COLORS[branch.code] || '#6b7280' }}
            />
            {branch.shortName} ({branch.percentage}%)
          </div>
        ))}
      </div>
      <div className="h-[240px]">
        <ResponsiveContainer width="100%" height="100%">
          <BarChart data={chartData} margin={{ top: 10, right: 10, left: -10, bottom: 0 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="var(--color-border)" />
            <XAxis
              dataKey="shortName"
              tick={{ fontSize: 12, fill: 'var(--color-text-muted)', fontWeight: 500 }}
              tickLine={false}
              axisLine={false}
            />
            <YAxis
              tickFormatter={formatYAxis}
              tick={{ fontSize: 11, fill: 'var(--color-text-muted)' }}
              tickLine={false}
              axisLine={false}
              width={50}
            />
            <Tooltip content={<CustomTooltip />} />
            <Bar dataKey="revenue" radius={[4, 4, 0, 0]}>
              {chartData.map((entry) => (
                <Cell key={entry.code} fill={BRANCH_COLORS[entry.code] || '#6b7280'} />
              ))}
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </div>
    </motion.div>
  )
}

// ============================================================================
// Revenue by Category Chart (Pie)
// ============================================================================
interface RevenueByCategoryChartProps {
  data: RevenueCategoryItem[]
}

const CATEGORY_COLORS = [
  '#10b981', // Emerald
  '#3b82f6', // Blue
  '#f59e0b', // Amber
  '#8b5cf6', // Purple
  '#ec4899', // Pink
  '#06b6d4', // Cyan
  '#f97316', // Orange
  '#6366f1', // Indigo
]

function RevenueByCategoryChart({ data }: RevenueByCategoryChartProps) {
  // Take top 7 categories, group rest as "Others"
  const topCategories = data.slice(0, 7)
  const otherCategories = data.slice(7)

  let chartData = topCategories
  if (otherCategories.length > 0) {
    const otherRevenue = otherCategories.reduce((sum, c) => sum + c.revenue, 0)
    const otherCount = otherCategories.reduce((sum, c) => sum + c.count, 0)
    const total = data.reduce((sum, c) => sum + c.revenue, 0)
    chartData = [
      ...topCategories,
      {
        category: 'Others',
        revenue: otherRevenue,
        count: otherCount,
        percentage: total > 0 ? Math.round((otherRevenue / total) * 100 * 10) / 10 : 0,
      },
    ]
  }

  const total = chartData.reduce((sum, c) => sum + c.revenue, 0)

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: 0.6, duration: 0.3 }}
      className="bg-card rounded-xl p-4 sm:p-6"
    >
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-sm sm:text-[15px] font-semibold text-text-dark">Revenue by Category</h3>
      </div>
      <div className="flex items-start gap-4">
        <div className="w-[160px] h-[200px] sm:w-[180px]">
          <ResponsiveContainer width="100%" height="100%">
            <PieChart>
              <Pie
                data={chartData as any[]}
                cx="50%"
                cy="50%"
                innerRadius={45}
                outerRadius={70}
                paddingAngle={2}
                dataKey="revenue"
                nameKey="category"
              >
                {chartData.map((_, index) => (
                  <Cell key={`cell-${index}`} fill={CATEGORY_COLORS[index % CATEGORY_COLORS.length]} />
                ))}
              </Pie>
              <Tooltip
                formatter={(value) => formatCurrency(value as number)}
                contentStyle={{
                  backgroundColor: 'var(--color-card)',
                  border: '1px solid var(--color-border)',
                  borderRadius: '8px',
                }}
              />
            </PieChart>
          </ResponsiveContainer>
        </div>
        {/* Legend */}
        <div className="flex-1 flex flex-col gap-2 max-h-[200px] overflow-y-auto">
          {chartData.map((item, index) => (
            <div key={item.category} className="flex items-center gap-2 text-xs">
              <span
                className="w-2.5 h-2.5 rounded-full shrink-0"
                style={{ backgroundColor: CATEGORY_COLORS[index % CATEGORY_COLORS.length] }}
              />
              <span className="text-text-dark truncate flex-1">{item.category}</span>
              <span className="text-text-muted font-medium">{item.percentage}%</span>
            </div>
          ))}
        </div>
      </div>
    </motion.div>
  )
}

// ============================================================================
// Monthly Comparison Table
// ============================================================================
interface MonthlyComparisonTableProps {
  data: MonthlyComparisonItem[]
}

function MonthlyComparisonTable({ data }: MonthlyComparisonTableProps) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: 0.7, duration: 0.3 }}
      className="bg-card rounded-xl p-4 sm:p-6"
    >
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-sm sm:text-[15px] font-semibold text-text-dark">Monthly Comparison</h3>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-border">
              <th className="text-left py-3 px-2 text-xs font-semibold text-text-muted uppercase tracking-wider">
                Month
              </th>
              <th className="text-right py-3 px-2 text-xs font-semibold text-text-muted uppercase tracking-wider">
                Revenue
              </th>
              <th className="text-right py-3 px-2 text-xs font-semibold text-text-muted uppercase tracking-wider hidden sm:table-cell">
                Invoices
              </th>
              <th className="text-right py-3 px-2 text-xs font-semibold text-text-muted uppercase tracking-wider hidden md:table-cell">
                Patients
              </th>
              <th className="text-right py-3 px-2 text-xs font-semibold text-text-muted uppercase tracking-wider hidden lg:table-cell">
                Avg Trans
              </th>
              <th className="text-right py-3 px-2 text-xs font-semibold text-text-muted uppercase tracking-wider">
                Growth
              </th>
            </tr>
          </thead>
          <tbody>
            {data.map((row, index) => {
              const isPositive = row.growth !== null && row.growth >= 0
              const showGrowth = row.growth !== null

              return (
                <tr
                  key={`${row.year}-${row.month}`}
                  className={cn(
                    'border-b border-border/50 hover:bg-muted/50 transition-colors',
                    index === 0 && 'bg-muted/30'
                  )}
                >
                  <td className="py-3 px-2 font-medium text-text-dark">{row.label}</td>
                  <td className="py-3 px-2 text-right font-semibold text-success">
                    {formatCurrency(row.revenue)}
                  </td>
                  <td className="py-3 px-2 text-right text-text-muted hidden sm:table-cell">
                    {row.invoices.toLocaleString()}
                  </td>
                  <td className="py-3 px-2 text-right text-text-muted hidden md:table-cell">
                    {row.patients.toLocaleString()}
                  </td>
                  <td className="py-3 px-2 text-right text-text-muted hidden lg:table-cell">
                    {formatCurrency(row.avgTransaction)}
                  </td>
                  <td className="py-3 px-2 text-right">
                    {showGrowth ? (
                      <span
                        className={cn(
                          'inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold',
                          isPositive ? 'bg-success/10 text-success' : 'bg-destructive/10 text-destructive'
                        )}
                      >
                        {isPositive ? (
                          <TrendingUp className="w-3 h-3" />
                        ) : (
                          <TrendingDown className="w-3 h-3" />
                        )}
                        {Math.abs(row.growth!)}%
                      </span>
                    ) : (
                      <span className="text-text-muted">—</span>
                    )}
                  </td>
                </tr>
              )
            })}
          </tbody>
        </table>
      </div>
    </motion.div>
  )
}

// ============================================================================
// Patient Tab Content
// ============================================================================
interface PatientTabContentProps {
  data: ReturnType<typeof usePatientAnalytics>['data']
  isPending: boolean
  isError: boolean
}

function PatientTabContent({ data, isPending, isError }: PatientTabContentProps) {
  if (isPending) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    )
  }

  if (isError) {
    return (
      <div className="text-center py-20 text-destructive">
        Failed to load patient analytics. Please try again.
      </div>
    )
  }

  if (!data) return null

  return (
    <div className="flex flex-col gap-5 lg:gap-7 pb-6 lg:pb-9">
      {/* Summary Cards */}
      <div className="grid gap-3 sm:gap-4.5 grid-cols-2 lg:grid-cols-5">
        <PatientSummaryCard
          title="Total Patients"
          value={formatNumber(data.summary.totalPatients)}
          icon={Users}
          delay={0}
        />
        <PatientSummaryCard
          title="New Patients"
          value={formatNumber(data.summary.newPatients)}
          icon={UserPlus}
          color="text-success"
          delay={0.1}
        />
        <PatientSummaryCard
          title="Returning"
          value={formatNumber(data.summary.returningPatients)}
          icon={Activity}
          color="text-primary"
          delay={0.2}
        />
        <PatientSummaryCard
          title="Avg Visits"
          value={data.summary.avgVisitsPerPatient.toFixed(1)}
          icon={Receipt}
          delay={0.3}
        />
        <PatientSummaryCard
          title="Inactive (6m+)"
          value={formatNumber(data.summary.inactiveCount)}
          icon={UserX}
          color="text-amber-500"
          delay={0.4}
        />
      </div>

      {/* Charts Row 1 */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 lg:gap-6">
        <AcquisitionTrendChart data={data.charts.acquisitionTrend} />
        <VisitFrequencyChart data={data.charts.visitFrequency} />
      </div>

      {/* Charts Row 2: Patients by Branch */}
      <PatientsByBranchChart data={data.charts.patientsByBranch} />

      {/* Tables Row */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 lg:gap-6">
        <TopPatientsTable data={data.topPatients} />
        <InactivePatientsTable data={data.inactivePatients} />
      </div>
    </div>
  )
}

// ============================================================================
// Patient Summary Card
// ============================================================================
interface PatientSummaryCardProps {
  title: string
  value: string
  icon: React.ElementType
  color?: string
  delay?: number
}

function PatientSummaryCard({ title, value, icon: Icon, color, delay = 0 }: PatientSummaryCardProps) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay, duration: 0.3 }}
      className="bg-card rounded-xl p-4 sm:p-5"
    >
      <div className="flex items-start justify-between mb-3">
        <div className="w-10 h-10 sm:w-11 sm:h-11 rounded-full bg-icon-beige flex items-center justify-center">
          <Icon className={cn('w-5 h-5 sm:w-[22px] sm:h-[22px]', color || 'text-foreground')} />
        </div>
      </div>
      <div className="text-xl sm:text-2xl font-bold text-text-dark mb-1">{value}</div>
      <div className="text-xs sm:text-sm text-text-muted">{title}</div>
    </motion.div>
  )
}

// ============================================================================
// Acquisition Trend Chart (Area)
// ============================================================================
interface AcquisitionTrendChartProps {
  data: AcquisitionTrendItem[]
}

function AcquisitionTrendChart({ data }: AcquisitionTrendChartProps) {
  const CustomTooltip = ({ active, payload, label }: any) => {
    if (active && payload && payload.length) {
      const item = payload[0].payload as AcquisitionTrendItem
      return (
        <div className="bg-card border border-border rounded-lg px-3 py-2 shadow-lg">
          <p className="text-sm font-medium text-foreground">{item.label}</p>
          <p className="text-sm text-success font-semibold">
            {item.newPatients} new patients
          </p>
          <p className="text-xs text-text-muted">
            {item.totalPatients} total patients this month
          </p>
        </div>
      )
    }
    return null
  }

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: 0.5, duration: 0.3 }}
      className="bg-card rounded-xl p-4 sm:p-6"
    >
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-sm sm:text-[15px] font-semibold text-text-dark">Patient Acquisition</h3>
        <span className="text-xs text-text-muted">New patients over time</span>
      </div>
      <div className="h-[280px]">
        <ResponsiveContainer width="100%" height="100%">
          <AreaChart data={data} margin={{ top: 10, right: 10, left: -10, bottom: 0 }}>
            <defs>
              <linearGradient id="acquisitionGradient" x1="0" y1="0" x2="0" y2="1">
                <stop offset="5%" stopColor="#10b981" stopOpacity={0.3} />
                <stop offset="95%" stopColor="#10b981" stopOpacity={0} />
              </linearGradient>
            </defs>
            <CartesianGrid strokeDasharray="3 3" stroke="var(--color-border)" />
            <XAxis
              dataKey="label"
              tick={{ fontSize: 10, fill: 'var(--color-text-muted)' }}
              tickLine={false}
              axisLine={false}
              interval="preserveStartEnd"
            />
            <YAxis
              tick={{ fontSize: 11, fill: 'var(--color-text-muted)' }}
              tickLine={false}
              axisLine={false}
              width={40}
            />
            <Tooltip content={<CustomTooltip />} />
            <Area
              type="monotone"
              dataKey="newPatients"
              stroke="#10b981"
              strokeWidth={2}
              fill="url(#acquisitionGradient)"
              name="New Patients"
            />
          </AreaChart>
        </ResponsiveContainer>
      </div>
    </motion.div>
  )
}

// ============================================================================
// Visit Frequency Chart (Horizontal Bar)
// ============================================================================
interface VisitFrequencyChartProps {
  data: VisitFrequencyItem[]
}

const FREQUENCY_COLORS = ['#ef4444', '#f59e0b', '#10b981', '#3b82f6', '#8b5cf6', '#06b6d4']

function VisitFrequencyChart({ data }: VisitFrequencyChartProps) {
  const CustomTooltip = ({ active, payload }: any) => {
    if (active && payload && payload.length) {
      const item = payload[0].payload as VisitFrequencyItem
      return (
        <div className="bg-card border border-border rounded-lg px-3 py-2 shadow-lg">
          <p className="text-sm font-medium text-foreground">{item.range}</p>
          <p className="text-sm text-primary font-semibold">
            {item.count.toLocaleString()} patients
          </p>
          <p className="text-xs text-text-muted">{item.percentage}% of total</p>
        </div>
      )
    }
    return null
  }

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: 0.6, duration: 0.3 }}
      className="bg-card rounded-xl p-4 sm:p-6"
    >
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-sm sm:text-[15px] font-semibold text-text-dark">Visit Frequency</h3>
        <span className="text-xs text-text-muted">Distribution by visits</span>
      </div>
      <div className="h-[280px]">
        <ResponsiveContainer width="100%" height="100%">
          <BarChart data={data} layout="vertical" margin={{ top: 5, right: 10, left: 60, bottom: 5 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="var(--color-border)" horizontal={false} />
            <XAxis type="number" tick={{ fontSize: 11, fill: 'var(--color-text-muted)' }} tickLine={false} />
            <YAxis
              dataKey="range"
              type="category"
              tick={{ fontSize: 11, fill: 'var(--color-text-muted)' }}
              tickLine={false}
              axisLine={false}
              width={60}
            />
            <Tooltip content={<CustomTooltip />} />
            <Bar dataKey="count" radius={[0, 4, 4, 0]}>
              {data.map((_, index) => (
                <Cell key={`cell-${index}`} fill={FREQUENCY_COLORS[index % FREQUENCY_COLORS.length]} />
              ))}
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </div>
    </motion.div>
  )
}

// ============================================================================
// Patients by Branch Chart (Bar)
// ============================================================================
interface PatientsByBranchChartProps {
  data: PatientsByBranchItem[]
}

function PatientsByBranchChart({ data }: PatientsByBranchChartProps) {
  const chartData = data.map((b) => ({
    ...b,
    shortName: BRANCH_SHORT_NAMES[b.code] || b.code.toUpperCase(),
  }))

  const CustomTooltip = ({ active, payload }: any) => {
    if (active && payload && payload.length) {
      const branch = payload[0].payload as PatientsByBranchItem & { shortName: string }
      return (
        <div className="bg-card border border-border rounded-lg px-3 py-2 shadow-lg">
          <p className="text-sm font-medium text-foreground">{branch.name}</p>
          <p className="text-sm text-primary font-semibold">
            {branch.count.toLocaleString()} patients
          </p>
          <p className="text-xs text-text-muted">{branch.percentage}% of total</p>
        </div>
      )
    }
    return null
  }

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: 0.7, duration: 0.3 }}
      className="bg-card rounded-xl p-4 sm:p-6"
    >
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-sm sm:text-[15px] font-semibold text-text-dark">Patients by Branch</h3>
      </div>
      {/* Legend */}
      <div className="flex flex-wrap gap-3 sm:gap-4 mb-4">
        {chartData.map((branch) => (
          <div key={branch.code} className="flex items-center gap-2 text-xs text-text-muted">
            <span
              className="w-2.5 h-2.5 rounded-full"
              style={{ backgroundColor: BRANCH_COLORS[branch.code] || '#6b7280' }}
            />
            {branch.shortName} ({branch.percentage}%)
          </div>
        ))}
      </div>
      <div className="h-[200px]">
        <ResponsiveContainer width="100%" height="100%">
          <BarChart data={chartData} margin={{ top: 10, right: 10, left: -10, bottom: 0 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="var(--color-border)" />
            <XAxis
              dataKey="shortName"
              tick={{ fontSize: 12, fill: 'var(--color-text-muted)', fontWeight: 500 }}
              tickLine={false}
              axisLine={false}
            />
            <YAxis
              tick={{ fontSize: 11, fill: 'var(--color-text-muted)' }}
              tickLine={false}
              axisLine={false}
              width={50}
            />
            <Tooltip content={<CustomTooltip />} />
            <Bar dataKey="count" radius={[4, 4, 0, 0]}>
              {chartData.map((entry) => (
                <Cell key={entry.code} fill={BRANCH_COLORS[entry.code] || '#6b7280'} />
              ))}
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </div>
    </motion.div>
  )
}

// ============================================================================
// Top Patients Table (VIP)
// ============================================================================
interface TopPatientsTableProps {
  data: TopPatientItem[]
}

function TopPatientsTable({ data }: TopPatientsTableProps) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: 0.8, duration: 0.3 }}
      className="bg-card rounded-xl p-4 sm:p-6"
    >
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <Crown className="w-4 h-4 text-amber-500" />
          <h3 className="text-sm sm:text-[15px] font-semibold text-text-dark">Top Patients</h3>
        </div>
        <span className="text-xs text-text-muted">By Revenue</span>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-border">
              <th className="text-left py-2 px-2 text-xs font-semibold text-text-muted uppercase tracking-wider">
                Patient
              </th>
              <th className="text-right py-2 px-2 text-xs font-semibold text-text-muted uppercase tracking-wider hidden sm:table-cell">
                Visits
              </th>
              <th className="text-right py-2 px-2 text-xs font-semibold text-text-muted uppercase tracking-wider">
                Spent
              </th>
            </tr>
          </thead>
          <tbody>
            {data.slice(0, 5).map((patient, index) => (
              <tr
                key={patient.id}
                className={cn(
                  'border-b border-border/50 hover:bg-muted/50 transition-colors',
                  index === 0 && 'bg-amber-500/5'
                )}
              >
                <td className="py-2.5 px-2">
                  <div className="flex items-center gap-2">
                    <span className="w-5 text-xs font-bold text-text-muted">{index + 1}</span>
                    <div>
                      <div className="font-medium text-text-dark truncate max-w-[140px]">
                        {patient.name}
                      </div>
                      <div className="text-xs text-text-muted">{patient.pno}</div>
                    </div>
                  </div>
                </td>
                <td className="py-2.5 px-2 text-right text-text-muted hidden sm:table-cell">
                  {patient.totalVisits}
                </td>
                <td className="py-2.5 px-2 text-right font-semibold text-success">
                  {formatCurrency(patient.totalSpent)}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </motion.div>
  )
}

// ============================================================================
// Inactive Patients Table (Alert)
// ============================================================================
interface InactivePatientsTableProps {
  data: InactivePatientItem[]
}

function InactivePatientsTable({ data }: InactivePatientsTableProps) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: 0.9, duration: 0.3 }}
      className="bg-card rounded-xl p-4 sm:p-6"
    >
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <AlertTriangle className="w-4 h-4 text-amber-500" />
          <h3 className="text-sm sm:text-[15px] font-semibold text-text-dark">Inactive Patients</h3>
        </div>
        <span className="text-xs text-text-muted">No visit in 6+ months</span>
      </div>
      {data.length === 0 ? (
        <div className="py-8 text-center text-text-muted text-sm">
          No inactive high-value patients found
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-border">
                <th className="text-left py-2 px-2 text-xs font-semibold text-text-muted uppercase tracking-wider">
                  Patient
                </th>
                <th className="text-right py-2 px-2 text-xs font-semibold text-text-muted uppercase tracking-wider hidden sm:table-cell">
                  Days
                </th>
                <th className="text-right py-2 px-2 text-xs font-semibold text-text-muted uppercase tracking-wider">
                  Value
                </th>
              </tr>
            </thead>
            <tbody>
              {data.slice(0, 5).map((patient, index) => (
                <tr
                  key={patient.id}
                  className="border-b border-border/50 hover:bg-muted/50 transition-colors"
                >
                  <td className="py-2.5 px-2">
                    <div className="flex items-center gap-2">
                      <div>
                        <div className="font-medium text-text-dark truncate max-w-[140px]">
                          {patient.name}
                        </div>
                        <div className="text-xs text-text-muted flex items-center gap-1">
                          {patient.phone && <Phone className="w-3 h-3" />}
                          {patient.pno}
                        </div>
                      </div>
                    </div>
                  </td>
                  <td className="py-2.5 px-2 text-right hidden sm:table-cell">
                    <span className="px-2 py-0.5 bg-amber-500/10 text-amber-600 text-xs font-semibold rounded-full">
                      {patient.daysSinceVisit}d
                    </span>
                  </td>
                  <td className="py-2.5 px-2 text-right font-semibold text-text-dark">
                    {formatCurrency(patient.totalSpent)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </motion.div>
  )
}

// ============================================================================
// Treatment Tab Content
// ============================================================================
interface TreatmentTabContentProps {
  data: ReturnType<typeof useTreatmentAnalytics>['data']
  isPending: boolean
  isError: boolean
}

function TreatmentTabContent({ data, isPending, isError }: TreatmentTabContentProps) {
  if (isPending) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    )
  }

  if (isError) {
    return (
      <div className="text-center py-20 text-destructive">
        Failed to load treatment analytics. Please try again.
      </div>
    )
  }

  if (!data) return null

  return (
    <div className="flex flex-col gap-5 lg:gap-7 pb-6 lg:pb-9">
      {/* Summary Cards */}
      <div className="grid gap-3 sm:gap-4.5 grid-cols-2 lg:grid-cols-5">
        <TreatmentSummaryCard
          title="Billed Items"
          value={formatNumber(data.summary.totalTreatments)}
          icon={Receipt}
          delay={0}
        />
        <TreatmentSummaryCard
          title="Unique Types"
          value={formatNumber(data.summary.uniqueTreatments)}
          icon={Activity}
          color="text-primary"
          delay={0.1}
        />
        <TreatmentSummaryCard
          title="Treatment Revenue"
          value={formatCurrency(data.summary.totalRevenue)}
          icon={TrendingUp}
          color="text-success"
          delay={0.2}
        />
        <TreatmentSummaryCard
          title="Avg per Item"
          value={formatCurrency(data.summary.avgRevenuePerTreatment)}
          icon={Receipt}
          delay={0.3}
        />
        <TreatmentSummaryCard
          title="Match Rate"
          value={`${data.summary.matchRate}%`}
          icon={Activity}
          color={data.summary.matchRate >= 80 ? 'text-success' : 'text-amber-500'}
          delay={0.4}
        />
      </div>

      {/* Charts Row 1 */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 lg:gap-6">
        <TreatmentTrendChart data={data.charts.treatmentTrend} />
        <TreatmentTypesChart data={data.charts.treatmentTypes} />
      </div>

      {/* Charts Row 2 */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 lg:gap-6">
        <TreatmentCategoryChart data={data.charts.categoryBreakdown} />
        <TreatmentByBranchChart data={data.charts.treatmentsByBranch} />
      </div>

      {/* Tables Row */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 lg:gap-6">
        <TopTreatmentsTable data={data.charts.popularTreatments} />
        <CrossSellTable data={data.crossSellAnalysis} />
      </div>
    </div>
  )
}

// ============================================================================
// Treatment Summary Card
// ============================================================================
interface TreatmentSummaryCardProps {
  title: string
  value: string
  icon: React.ElementType
  color?: string
  delay?: number
}

function TreatmentSummaryCard({ title, value, icon: Icon, color, delay = 0 }: TreatmentSummaryCardProps) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay, duration: 0.3 }}
      className="bg-card rounded-xl p-4 sm:p-5"
    >
      <div className="flex items-start justify-between mb-3">
        <div className="w-10 h-10 sm:w-11 sm:h-11 rounded-full bg-icon-beige flex items-center justify-center">
          <Icon className={cn('w-5 h-5 sm:w-[22px] sm:h-[22px]', color || 'text-foreground')} />
        </div>
      </div>
      <div className="text-xl sm:text-2xl font-bold text-text-dark mb-1">{value}</div>
      <div className="text-xs sm:text-sm text-text-muted">{title}</div>
    </motion.div>
  )
}

// ============================================================================
// Treatment Trend Chart (Area)
// ============================================================================
interface TreatmentTrendChartProps {
  data: TreatmentTrendItem[]
}

function TreatmentTrendChart({ data }: TreatmentTrendChartProps) {
  const formatYAxis = (value: number) => {
    if (value >= 1000000) return `${(value / 1000000).toFixed(1)}M`
    if (value >= 1000) return `${(value / 1000).toFixed(0)}K`
    return value.toString()
  }

  const CustomTooltip = ({ active, payload }: any) => {
    if (active && payload && payload.length) {
      const item = payload[0].payload as TreatmentTrendItem
      return (
        <div className="bg-card border border-border rounded-lg px-3 py-2 shadow-lg">
          <p className="text-sm font-medium text-foreground">{item.label}</p>
          <p className="text-sm text-primary font-semibold">
            {item.treatmentCount.toLocaleString()} treatments
          </p>
          <p className="text-xs text-text-muted">
            {item.uniqueTreatments} types | {formatCurrency(item.revenue)}
          </p>
        </div>
      )
    }
    return null
  }

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: 0.5, duration: 0.3 }}
      className="bg-card rounded-xl p-4 sm:p-6"
    >
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-sm sm:text-[15px] font-semibold text-text-dark">Treatment Volume</h3>
        <span className="text-xs text-text-muted">Treatments over time</span>
      </div>
      <div className="h-[280px]">
        <ResponsiveContainer width="100%" height="100%">
          <AreaChart data={data} margin={{ top: 10, right: 10, left: -10, bottom: 0 }}>
            <defs>
              <linearGradient id="treatmentGradient" x1="0" y1="0" x2="0" y2="1">
                <stop offset="5%" stopColor="#8b5cf6" stopOpacity={0.3} />
                <stop offset="95%" stopColor="#8b5cf6" stopOpacity={0} />
              </linearGradient>
            </defs>
            <CartesianGrid strokeDasharray="3 3" stroke="var(--color-border)" />
            <XAxis
              dataKey="label"
              tick={{ fontSize: 10, fill: 'var(--color-text-muted)' }}
              tickLine={false}
              axisLine={false}
              interval="preserveStartEnd"
            />
            <YAxis
              tickFormatter={formatYAxis}
              tick={{ fontSize: 11, fill: 'var(--color-text-muted)' }}
              tickLine={false}
              axisLine={false}
              width={40}
            />
            <Tooltip content={<CustomTooltip />} />
            <Area
              type="monotone"
              dataKey="treatmentCount"
              stroke="#8b5cf6"
              strokeWidth={2}
              fill="url(#treatmentGradient)"
              name="Treatments"
            />
          </AreaChart>
        </ResponsiveContainer>
      </div>
    </motion.div>
  )
}

// ============================================================================
// Treatment Types Chart (Donut)
// ============================================================================
interface TreatmentTypesChartProps {
  data: TreatmentTypeItem[]
}

const TREATMENT_TYPE_COLORS = ['#10b981', '#3b82f6', '#f59e0b']

function TreatmentTypesChart({ data }: TreatmentTypesChartProps) {
  const total = data.reduce((sum, item) => sum + item.count, 0)

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: 0.6, duration: 0.3 }}
      className="bg-card rounded-xl p-4 sm:p-6"
    >
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-sm sm:text-[15px] font-semibold text-text-dark">Treatment Types</h3>
      </div>
      <div className="flex items-center gap-6">
        <div className="w-[160px] h-[160px] sm:w-[180px] sm:h-[180px]">
          <ResponsiveContainer width="100%" height="100%">
            <PieChart>
              <Pie
                data={data as any[]}
                cx="50%"
                cy="50%"
                innerRadius={50}
                outerRadius={75}
                paddingAngle={2}
                dataKey="count"
                nameKey="type"
              >
                {data.map((_, index) => (
                  <Cell key={`cell-${index}`} fill={TREATMENT_TYPE_COLORS[index % TREATMENT_TYPE_COLORS.length]} />
                ))}
              </Pie>
              <Tooltip
                formatter={(value, name) => [
                  `${(value as number).toLocaleString()} (${total > 0 ? Math.round(((value as number) / total) * 100) : 0}%)`,
                  name,
                ]}
                contentStyle={{
                  backgroundColor: 'var(--color-card)',
                  border: '1px solid var(--color-border)',
                  borderRadius: '8px',
                }}
              />
            </PieChart>
          </ResponsiveContainer>
        </div>
        {/* Legend */}
        <div className="flex flex-col gap-3">
          {data.map((item, index) => {
            const percentage = total > 0 ? Math.round((item.count / total) * 100) : 0
            return (
              <div key={item.type} className="flex items-center gap-3">
                <span
                  className="w-3 h-3 rounded-full"
                  style={{ backgroundColor: TREATMENT_TYPE_COLORS[index % TREATMENT_TYPE_COLORS.length] }}
                />
                <div>
                  <div className="text-sm font-medium text-text-dark">
                    {item.type} ({percentage}%)
                  </div>
                  <div className="text-xs text-text-muted">
                    {item.count.toLocaleString()} items | {formatCurrency(item.revenue)}
                  </div>
                </div>
              </div>
            )
          })}
        </div>
      </div>
    </motion.div>
  )
}

// ============================================================================
// Treatment Category Chart (Pie)
// ============================================================================
interface TreatmentCategoryChartProps {
  data: CategoryBreakdownItem[]
}

function TreatmentCategoryChart({ data }: TreatmentCategoryChartProps) {
  // Take top 7 categories, group rest as "Others"
  const topCategories = data.slice(0, 7)
  const otherCategories = data.slice(7)

  let chartData = topCategories
  if (otherCategories.length > 0) {
    const otherRevenue = otherCategories.reduce((sum, c) => sum + c.revenue, 0)
    const otherCount = otherCategories.reduce((sum, c) => sum + c.count, 0)
    const total = data.reduce((sum, c) => sum + c.revenue, 0)
    chartData = [
      ...topCategories,
      {
        category: 'Others',
        revenue: otherRevenue,
        count: otherCount,
        percentage: total > 0 ? Math.round((otherRevenue / total) * 100 * 10) / 10 : 0,
      },
    ]
  }

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: 0.7, duration: 0.3 }}
      className="bg-card rounded-xl p-4 sm:p-6"
    >
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-sm sm:text-[15px] font-semibold text-text-dark">Revenue by Category</h3>
      </div>
      <div className="flex items-start gap-4">
        <div className="w-[160px] h-[200px] sm:w-[180px]">
          <ResponsiveContainer width="100%" height="100%">
            <PieChart>
              <Pie
                data={chartData as any[]}
                cx="50%"
                cy="50%"
                innerRadius={45}
                outerRadius={70}
                paddingAngle={2}
                dataKey="revenue"
                nameKey="category"
              >
                {chartData.map((_, index) => (
                  <Cell key={`cell-${index}`} fill={CATEGORY_COLORS[index % CATEGORY_COLORS.length]} />
                ))}
              </Pie>
              <Tooltip
                formatter={(value) => formatCurrency(value as number)}
                contentStyle={{
                  backgroundColor: 'var(--color-card)',
                  border: '1px solid var(--color-border)',
                  borderRadius: '8px',
                }}
              />
            </PieChart>
          </ResponsiveContainer>
        </div>
        {/* Legend */}
        <div className="flex-1 flex flex-col gap-2 max-h-[200px] overflow-y-auto">
          {chartData.map((item, index) => (
            <div key={item.category} className="flex items-center gap-2 text-xs">
              <span
                className="w-2.5 h-2.5 rounded-full shrink-0"
                style={{ backgroundColor: CATEGORY_COLORS[index % CATEGORY_COLORS.length] }}
              />
              <span className="text-text-dark truncate flex-1">{item.category}</span>
              <span className="text-text-muted font-medium">{item.percentage}%</span>
            </div>
          ))}
        </div>
      </div>
    </motion.div>
  )
}

// ============================================================================
// Treatment by Branch Chart (Bar)
// ============================================================================
interface TreatmentByBranchChartProps {
  data: TreatmentsByBranchItem[]
}

function TreatmentByBranchChart({ data }: TreatmentByBranchChartProps) {
  const chartData = data.map((b) => ({
    ...b,
    shortName: BRANCH_SHORT_NAMES[b.code] || b.code.toUpperCase(),
  }))

  const formatYAxis = (value: number) => {
    if (value >= 1000000) return `${(value / 1000000).toFixed(1)}M`
    if (value >= 1000) return `${(value / 1000).toFixed(0)}K`
    return value.toString()
  }

  const CustomTooltip = ({ active, payload }: any) => {
    if (active && payload && payload.length) {
      const branch = payload[0].payload as TreatmentsByBranchItem & { shortName: string }
      return (
        <div className="bg-card border border-border rounded-lg px-3 py-2 shadow-lg">
          <p className="text-sm font-medium text-foreground">{branch.name}</p>
          <p className="text-sm text-success font-semibold">
            {formatCurrency(branch.revenue)}
          </p>
          <p className="text-xs text-text-muted">
            {branch.treatmentCount.toLocaleString()} treatments | {branch.percentage}%
          </p>
        </div>
      )
    }
    return null
  }

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: 0.8, duration: 0.3 }}
      className="bg-card rounded-xl p-4 sm:p-6"
    >
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-sm sm:text-[15px] font-semibold text-text-dark">Revenue by Branch</h3>
      </div>
      {/* Legend */}
      <div className="flex flex-wrap gap-3 sm:gap-4 mb-4">
        {chartData.map((branch) => (
          <div key={branch.code} className="flex items-center gap-2 text-xs text-text-muted">
            <span
              className="w-2.5 h-2.5 rounded-full"
              style={{ backgroundColor: BRANCH_COLORS[branch.code] || '#6b7280' }}
            />
            {branch.shortName} ({branch.percentage}%)
          </div>
        ))}
      </div>
      <div className="h-[200px]">
        <ResponsiveContainer width="100%" height="100%">
          <BarChart data={chartData} margin={{ top: 10, right: 10, left: -10, bottom: 0 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="var(--color-border)" />
            <XAxis
              dataKey="shortName"
              tick={{ fontSize: 12, fill: 'var(--color-text-muted)', fontWeight: 500 }}
              tickLine={false}
              axisLine={false}
            />
            <YAxis
              tickFormatter={formatYAxis}
              tick={{ fontSize: 11, fill: 'var(--color-text-muted)' }}
              tickLine={false}
              axisLine={false}
              width={50}
            />
            <Tooltip content={<CustomTooltip />} />
            <Bar dataKey="revenue" radius={[4, 4, 0, 0]}>
              {chartData.map((entry) => (
                <Cell key={entry.code} fill={BRANCH_COLORS[entry.code] || '#6b7280'} />
              ))}
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </div>
    </motion.div>
  )
}

// ============================================================================
// Top Treatments Table
// ============================================================================
interface TopTreatmentsTableProps {
  data: PopularTreatmentItem[]
}

function TopTreatmentsTable({ data }: TopTreatmentsTableProps) {
  const maxRevenue = Math.max(...data.map((t) => t.revenue))

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: 0.9, duration: 0.3 }}
      className="bg-card rounded-xl p-4 sm:p-6"
    >
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <Stethoscope className="w-4 h-4 text-primary" />
          <h3 className="text-sm sm:text-[15px] font-semibold text-text-dark">Top Treatments</h3>
        </div>
        <span className="text-xs text-text-muted">By Revenue</span>
      </div>
      <div className="flex flex-col gap-3 max-h-[320px] overflow-y-auto">
        {data.slice(0, 10).map((treatment, index) => {
          const percentage = maxRevenue > 0 ? (treatment.revenue / maxRevenue) * 100 : 0
          return (
            <div key={`${treatment.name}-${index}`} className="flex items-center gap-3">
              <span className="w-5 text-xs font-semibold text-text-muted text-right">
                {index + 1}.
              </span>
              <div className="flex-1">
                <div className="flex items-center justify-between mb-1">
                  <span className="text-sm font-medium text-text-dark truncate max-w-[180px]">
                    {treatment.name}
                  </span>
                  <span className="text-sm font-semibold text-success">
                    {formatCurrency(treatment.revenue)}
                  </span>
                </div>
                <div className="h-2 bg-muted rounded-full overflow-hidden">
                  <motion.div
                    initial={{ width: 0 }}
                    animate={{ width: `${percentage}%` }}
                    transition={{ delay: 1.0 + index * 0.03, duration: 0.4 }}
                    className="h-full bg-gradient-to-r from-purple-500 to-purple-400 rounded-full"
                  />
                </div>
                <div className="flex items-center justify-between mt-1">
                  <span className="text-xs text-text-muted">
                    {treatment.category}
                  </span>
                  <span className="text-xs text-text-muted">
                    {treatment.count.toLocaleString()} × RM {treatment.avgPrice.toFixed(0)}
                  </span>
                </div>
              </div>
            </div>
          )
        })}
      </div>
    </motion.div>
  )
}

// ============================================================================
// Cross-sell Analysis Table
// ============================================================================
interface CrossSellTableProps {
  data: CrossSellItem[]
}

function CrossSellTable({ data }: CrossSellTableProps) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: 1.0, duration: 0.3 }}
      className="bg-card rounded-xl p-4 sm:p-6"
    >
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <Activity className="w-4 h-4 text-amber-500" />
          <h3 className="text-sm sm:text-[15px] font-semibold text-text-dark">Cross-sell Insights</h3>
        </div>
        <span className="text-xs text-text-muted">Bought together (5+)</span>
      </div>
      {data.length === 0 ? (
        <div className="py-8 text-center text-text-muted text-sm">
          No cross-sell patterns found in selected period
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-border">
                <th className="text-left py-2 px-2 text-xs font-semibold text-text-muted uppercase tracking-wider">
                  Treatment 1
                </th>
                <th className="text-center py-2 px-2 text-xs font-semibold text-text-muted uppercase tracking-wider">
                  +
                </th>
                <th className="text-left py-2 px-2 text-xs font-semibold text-text-muted uppercase tracking-wider">
                  Treatment 2
                </th>
                <th className="text-right py-2 px-2 text-xs font-semibold text-text-muted uppercase tracking-wider">
                  Count
                </th>
              </tr>
            </thead>
            <tbody>
              {data.slice(0, 8).map((pair, index) => (
                <tr
                  key={`${pair.treatment1}-${pair.treatment2}-${index}`}
                  className="border-b border-border/50 hover:bg-muted/50 transition-colors"
                >
                  <td className="py-2.5 px-2">
                    <span className="font-medium text-text-dark truncate block max-w-[120px]">
                      {pair.treatment1}
                    </span>
                  </td>
                  <td className="py-2.5 px-2 text-center text-text-muted">
                    &
                  </td>
                  <td className="py-2.5 px-2">
                    <span className="font-medium text-text-dark truncate block max-w-[120px]">
                      {pair.treatment2}
                    </span>
                  </td>
                  <td className="py-2.5 px-2 text-right">
                    <span className="px-2 py-0.5 bg-primary/10 text-primary text-xs font-semibold rounded-full">
                      {pair.pairCount}×
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </motion.div>
  )
}

// ============================================================================
// Report Tab Content
// ============================================================================
interface ReportTabContentProps {
  dateRange: DateRange | undefined
  branchId: string
  branchName: string
}

function ReportTabContent({ dateRange, branchId, branchName }: ReportTabContentProps) {
  const { data: templatesData, isPending: templatesPending } = useReportTemplates()
  const generateReport = useGenerateReport()
  const [selectedTemplate, setSelectedTemplate] = React.useState<ReportTemplateId | null>(null)
  const [generatedReport, setGeneratedReport] = React.useState<GeneratedReport | null>(null)

  // Icon mapping for templates
  const TEMPLATE_ICONS: Record<string, React.ElementType> = {
    TrendingUp,
    Users,
    Stethoscope,
    Building2,
    Receipt,
    Target,
  }

  const handleGenerateReport = async (templateId: ReportTemplateId) => {
    if (!dateRange?.from || !dateRange?.to) return

    setSelectedTemplate(templateId)
    setGeneratedReport(null)

    try {
      const result = await generateReport.mutateAsync({
        templateId,
        branchId: branchId || undefined,
        startDate: format(dateRange.from, 'yyyy-MM-dd'),
        endDate: format(dateRange.to, 'yyyy-MM-dd'),
      })

      if (result.success) {
        setGeneratedReport(result.report)
      }
    } catch (error) {
      console.error('Failed to generate report:', error)
    }
  }

  const handleExportCSV = () => {
    if (!generatedReport) return

    const { template, data } = generatedReport

    // Export based on template type
    if (template.id === 'monthly-revenue') {
      const reportData = data as MonthlyRevenueReportData
      exportDataToCSV(
        reportData.monthlyBreakdown.map(m => ({
          month: m.label,
          revenue: m.revenue.toFixed(2),
          invoices: m.invoices,
          patients: m.patients,
          avgTransaction: m.avgTransaction.toFixed(2),
          growth: m.growth !== null ? `${m.growth}%` : '-',
        })),
        [
          { key: 'month', header: 'Month' },
          { key: 'revenue', header: 'Revenue (RM)' },
          { key: 'invoices', header: 'Invoices' },
          { key: 'patients', header: 'Patients' },
          { key: 'avgTransaction', header: 'Avg Transaction (RM)' },
          { key: 'growth', header: 'Growth' },
        ],
        { filename: `${template.id}-${format(new Date(), 'yyyy-MM-dd')}` }
      )
    } else if (template.id === 'branch-comparison') {
      const reportData = data as BranchComparisonReportData
      exportDataToCSV(
        reportData.branches.map(b => ({
          branch: b.name,
          revenue: b.revenue.toFixed(2),
          invoices: b.invoices,
          patients: b.patients,
          newPatients: b.newPatients,
          avgTransaction: b.avgTransaction.toFixed(2),
          percentage: `${b.revenuePercent}%`,
        })),
        [
          { key: 'branch', header: 'Branch' },
          { key: 'revenue', header: 'Revenue (RM)' },
          { key: 'invoices', header: 'Invoices' },
          { key: 'patients', header: 'Patients' },
          { key: 'newPatients', header: 'New Patients' },
          { key: 'avgTransaction', header: 'Avg Transaction (RM)' },
          { key: 'percentage', header: 'Share (%)' },
        ],
        { filename: `${template.id}-${format(new Date(), 'yyyy-MM-dd')}` }
      )
    } else if (template.id === 'treatment-performance') {
      const reportData = data as TreatmentPerformanceReportData
      exportDataToCSV(
        reportData.topTreatments.map(t => ({
          treatment: t.name,
          category: t.category,
          count: t.count,
          revenue: t.revenue.toFixed(2),
        })),
        [
          { key: 'treatment', header: 'Treatment' },
          { key: 'category', header: 'Category' },
          { key: 'count', header: 'Count' },
          { key: 'revenue', header: 'Revenue (RM)' },
        ],
        { filename: `${template.id}-${format(new Date(), 'yyyy-MM-dd')}` }
      )
    } else if (template.id === 'patient-analytics') {
      const reportData = data as PatientAnalyticsReportData
      exportDataToCSV(
        reportData.topPatients.map(p => ({
          name: p.name,
          pno: p.pno,
          visits: p.visits,
          totalSpent: p.totalSpent.toFixed(2),
        })),
        [
          { key: 'name', header: 'Patient Name' },
          { key: 'pno', header: 'PNO' },
          { key: 'visits', header: 'Visits' },
          { key: 'totalSpent', header: 'Total Spent (RM)' },
        ],
        { filename: `${template.id}-${format(new Date(), 'yyyy-MM-dd')}` }
      )
    }
  }

  const handleExportPDF = () => {
    if (!generatedReport) return

    const { template, data, period } = generatedReport
    let content = ''

    // Generate PDF content based on template type
    if (template.id === 'monthly-revenue') {
      const reportData = data as MonthlyRevenueReportData
      content += generateSummaryCardsHTML([
        { label: 'Total Revenue', value: formatCurrencyForExport(reportData.summary.totalRevenue) },
        { label: 'Total Invoices', value: reportData.summary.totalInvoices.toLocaleString() },
        { label: 'Total Patients', value: reportData.summary.totalPatients.toLocaleString() },
        { label: 'Avg Monthly', value: formatCurrencyForExport(reportData.summary.avgMonthlyRevenue) },
        { label: 'Avg Transaction', value: formatCurrencyForExport(reportData.summary.avgTransaction) },
      ])
      content += '<div class="section-title">Monthly Breakdown</div>'
      content += generateHTMLTable(
        reportData.monthlyBreakdown,
        [
          { key: 'label', header: 'Month' },
          { key: 'revenue', header: 'Revenue (RM)', align: 'right', format: (v) => formatCurrencyForExport(v as number) },
          { key: 'invoices', header: 'Invoices', align: 'right', format: (v) => (v as number).toLocaleString() },
          { key: 'patients', header: 'Patients', align: 'right', format: (v) => (v as number).toLocaleString() },
          { key: 'growth', header: 'Growth', align: 'right', format: (v) => v !== null ? `${v}%` : '-' },
        ]
      )
    } else if (template.id === 'branch-comparison') {
      const reportData = data as BranchComparisonReportData
      content += generateSummaryCardsHTML([
        { label: 'Total Revenue', value: formatCurrencyForExport(reportData.summary.totalRevenue) },
        { label: 'Total Invoices', value: reportData.summary.totalInvoices.toLocaleString() },
        { label: 'Total Patients', value: reportData.summary.totalPatients.toLocaleString() },
        { label: 'Top Branch', value: reportData.summary.topBranch },
      ])
      content += '<div class="section-title">Branch Performance</div>'
      content += generateHTMLTable(
        reportData.branches,
        [
          { key: 'name', header: 'Branch' },
          { key: 'revenue', header: 'Revenue (RM)', align: 'right', format: (v) => formatCurrencyForExport(v as number) },
          { key: 'invoices', header: 'Invoices', align: 'right', format: (v) => (v as number).toLocaleString() },
          { key: 'patients', header: 'Patients', align: 'right', format: (v) => (v as number).toLocaleString() },
          { key: 'revenuePercent', header: 'Share (%)', align: 'right', format: (v) => `${v}%` },
        ]
      )
    } else if (template.id === 'treatment-performance') {
      const reportData = data as TreatmentPerformanceReportData
      content += generateSummaryCardsHTML([
        { label: 'Total Billed Items', value: reportData.summary.totalTreatments.toLocaleString() },
        { label: 'Unique Treatments', value: reportData.summary.uniqueTreatments.toLocaleString() },
        { label: 'Total Revenue', value: formatCurrencyForExport(reportData.summary.totalRevenue) },
        { label: 'Avg Price', value: formatCurrencyForExport(reportData.summary.avgPrice) },
      ])
      content += '<div class="section-title">Top Treatments</div>'
      content += generateHTMLTable(
        reportData.topTreatments,
        [
          { key: 'name', header: 'Treatment' },
          { key: 'category', header: 'Category' },
          { key: 'count', header: 'Count', align: 'right', format: (v) => (v as number).toLocaleString() },
          { key: 'revenue', header: 'Revenue (RM)', align: 'right', format: (v) => formatCurrencyForExport(v as number) },
        ]
      )
    } else if (template.id === 'patient-analytics') {
      const reportData = data as PatientAnalyticsReportData
      content += generateSummaryCardsHTML([
        { label: 'Total Patients', value: reportData.summary.totalPatients.toLocaleString() },
        { label: 'New Patients', value: reportData.summary.newPatients.toLocaleString() },
        { label: 'Returning', value: reportData.summary.returningPatients.toLocaleString() },
        { label: 'Retention Rate', value: `${reportData.summary.retentionRate}%` },
        { label: 'Avg Visits', value: reportData.summary.avgVisits.toFixed(1) },
      ])
      content += '<div class="section-title">Top Patients by Revenue</div>'
      content += generateHTMLTable(
        reportData.topPatients,
        [
          { key: 'name', header: 'Patient Name' },
          { key: 'pno', header: 'PNO' },
          { key: 'visits', header: 'Visits', align: 'right' },
          { key: 'totalSpent', header: 'Total Spent (RM)', align: 'right', format: (v) => formatCurrencyForExport(v as number) },
        ]
      )
    } else if (template.id === 'financial-summary') {
      const reportData = data as FinancialSummaryReportData
      content += generateSummaryCardsHTML([
        { label: 'Total Revenue', value: formatCurrencyForExport(reportData.summary.totalRevenue) },
        { label: 'Total Invoices', value: reportData.summary.totalInvoices.toLocaleString() },
        { label: 'Unique Patients', value: reportData.summary.uniquePatients.toLocaleString() },
        { label: 'Avg Daily Revenue', value: formatCurrencyForExport(reportData.summary.avgDailyRevenue) },
        { label: 'Avg Transaction', value: formatCurrencyForExport(reportData.summary.avgTransaction) },
      ])
      content += '<div class="section-title">Revenue by Category</div>'
      content += generateHTMLTable(
        reportData.revenueByCategory,
        [
          { key: 'category', header: 'Category' },
          { key: 'revenue', header: 'Revenue (RM)', align: 'right', format: (v) => formatCurrencyForExport(v as number) },
          { key: 'percentage', header: 'Share (%)', align: 'right', format: (v) => `${v}%` },
        ]
      )
    } else if (template.id === 'marketing-insights') {
      const reportData = data as MarketingInsightsReportData
      content += generateSummaryCardsHTML([
        { label: 'New Patients', value: reportData.summary.totalNewPatients.toLocaleString() },
        { label: 'Returning Patients', value: reportData.summary.totalReturningPatients.toLocaleString() },
        { label: 'Acquisition Rate', value: `${reportData.summary.acquisitionRate}%` },
        { label: 'Retention Rate', value: `${reportData.summary.retentionRate}%` },
      ])
      content += '<div class="section-title">Inactive High-Value Patients</div>'
      content += generateHTMLTable(
        reportData.inactivePatients,
        [
          { key: 'name', header: 'Patient Name' },
          { key: 'pno', header: 'PNO' },
          { key: 'daysSince', header: 'Days Inactive', align: 'right' },
          { key: 'totalSpent', header: 'Lifetime Value (RM)', align: 'right', format: (v) => formatCurrencyForExport(v as number) },
        ]
      )
    }

    printReportAsPDF(template.name, content, {
      subtitle: template.description,
      branchName: branchId === 'all' ? 'All Branches' : branchName,
      period,
    })
  }

  if (templatesPending) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    )
  }

  return (
    <div className="flex flex-col gap-5 lg:gap-7 pb-6 lg:pb-9">
      {/* Report Templates Grid */}
      <div className="grid gap-4 grid-cols-1 sm:grid-cols-2 lg:grid-cols-3">
        {templatesData?.templates.map((template, index) => {
          const Icon = TEMPLATE_ICONS[template.icon] || FileText
          const isSelected = selectedTemplate === template.id
          const isGenerating = isSelected && generateReport.isPending

          return (
            <ReportTemplateCard
              key={template.id}
              template={template}
              icon={Icon}
              isSelected={isSelected}
              isGenerating={isGenerating}
              delay={index * 0.05}
              onGenerate={() => handleGenerateReport(template.id)}
            />
          )
        })}
      </div>

      {/* Generated Report Preview */}
      {generatedReport && (
        <GeneratedReportPreview
          report={generatedReport}
          onExportCSV={handleExportCSV}
          onExportPDF={handleExportPDF}
        />
      )}

      {/* Loading state for report generation */}
      {generateReport.isPending && !generatedReport && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-card rounded-xl p-8 flex items-center justify-center gap-3"
        >
          <Loader2 className="w-5 h-5 animate-spin text-primary" />
          <span className="text-text-muted">Generating report...</span>
        </motion.div>
      )}
    </div>
  )
}

// ============================================================================
// Report Template Card
// ============================================================================
interface ReportTemplateCardProps {
  template: ReportTemplate
  icon: React.ElementType
  isSelected: boolean
  isGenerating: boolean
  delay: number
  onGenerate: () => void
}

function ReportTemplateCard({
  template,
  icon: Icon,
  isSelected,
  isGenerating,
  delay,
  onGenerate,
}: ReportTemplateCardProps) {
  const categoryColors: Record<string, string> = {
    financial: 'bg-emerald-500/10 text-emerald-600',
    patients: 'bg-blue-500/10 text-blue-600',
    treatments: 'bg-purple-500/10 text-purple-600',
    branches: 'bg-amber-500/10 text-amber-600',
    marketing: 'bg-pink-500/10 text-pink-600',
  }

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay, duration: 0.3 }}
      className={cn(
        'bg-card rounded-xl p-4 sm:p-5 border-2 transition-all cursor-pointer hover:border-primary/30',
        isSelected ? 'border-primary' : 'border-transparent'
      )}
      onClick={onGenerate}
    >
      <div className="flex items-start justify-between mb-3">
        <div className="w-10 h-10 sm:w-11 sm:h-11 rounded-full bg-icon-beige flex items-center justify-center">
          <Icon className="w-5 h-5 sm:w-[22px] sm:h-[22px] text-foreground" />
        </div>
        <span className={cn('px-2 py-0.5 rounded-full text-xs font-medium', categoryColors[template.category] || 'bg-muted text-text-muted')}>
          {template.category}
        </span>
      </div>
      <h3 className="text-sm sm:text-[15px] font-semibold text-text-dark mb-1.5">{template.name}</h3>
      <p className="text-xs text-text-muted line-clamp-2 mb-3">{template.description}</p>
      <Button
        size="sm"
        variant={isSelected ? 'default' : 'outline'}
        className="w-full gap-2"
        disabled={isGenerating}
      >
        {isGenerating ? (
          <>
            <Loader2 className="w-3.5 h-3.5 animate-spin" />
            Generating...
          </>
        ) : isSelected ? (
          <>
            <CheckCircle2 className="w-3.5 h-3.5" />
            Generated
          </>
        ) : (
          <>
            <FileText className="w-3.5 h-3.5" />
            Generate
          </>
        )}
      </Button>
    </motion.div>
  )
}

// ============================================================================
// Generated Report Preview
// ============================================================================
interface GeneratedReportPreviewProps {
  report: GeneratedReport
  onExportCSV: () => void
  onExportPDF: () => void
}

function GeneratedReportPreview({ report, onExportCSV, onExportPDF }: GeneratedReportPreviewProps) {
  const { template, period, branchId, generatedAt, data } = report

  const renderReportContent = () => {
    switch (template.id) {
      case 'monthly-revenue': {
        const reportData = data as MonthlyRevenueReportData
        return (
          <>
            {/* Summary */}
            <div className="grid gap-3 grid-cols-2 sm:grid-cols-5 mb-6">
              <ReportMetricCard label="Total Revenue" value={formatCurrency(reportData.summary.totalRevenue)} />
              <ReportMetricCard label="Invoices" value={formatNumber(reportData.summary.totalInvoices)} />
              <ReportMetricCard label="Patients" value={formatNumber(reportData.summary.totalPatients)} />
              <ReportMetricCard label="Avg Monthly" value={formatCurrency(reportData.summary.avgMonthlyRevenue)} />
              <ReportMetricCard label="Avg Transaction" value={formatCurrency(reportData.summary.avgTransaction)} />
            </div>
            {/* Table */}
            <ReportDataTable
              columns={['Month', 'Revenue', 'Invoices', 'Patients', 'Growth']}
              data={reportData.monthlyBreakdown.map(m => [
                m.label,
                formatCurrency(m.revenue),
                formatNumber(m.invoices),
                formatNumber(m.patients),
                m.growth !== null ? `${m.growth > 0 ? '+' : ''}${m.growth}%` : '-',
              ])}
            />
          </>
        )
      }
      case 'branch-comparison': {
        const reportData = data as BranchComparisonReportData
        return (
          <>
            <div className="grid gap-3 grid-cols-2 sm:grid-cols-4 mb-6">
              <ReportMetricCard label="Total Revenue" value={formatCurrency(reportData.summary.totalRevenue)} />
              <ReportMetricCard label="Total Invoices" value={formatNumber(reportData.summary.totalInvoices)} />
              <ReportMetricCard label="Total Patients" value={formatNumber(reportData.summary.totalPatients)} />
              <ReportMetricCard label="Top Branch" value={reportData.summary.topBranch} />
            </div>
            <ReportDataTable
              columns={['Branch', 'Revenue', 'Invoices', 'Patients', 'Share']}
              data={reportData.branches.map(b => [
                b.name,
                formatCurrency(b.revenue),
                formatNumber(b.invoices),
                formatNumber(b.patients),
                `${b.revenuePercent}%`,
              ])}
            />
          </>
        )
      }
      case 'treatment-performance': {
        const reportData = data as TreatmentPerformanceReportData
        return (
          <>
            <div className="grid gap-3 grid-cols-2 sm:grid-cols-4 mb-6">
              <ReportMetricCard label="Billed Items" value={formatNumber(reportData.summary.totalTreatments)} />
              <ReportMetricCard label="Unique Types" value={formatNumber(reportData.summary.uniqueTreatments)} />
              <ReportMetricCard label="Total Revenue" value={formatCurrency(reportData.summary.totalRevenue)} />
              <ReportMetricCard label="Avg Price" value={formatCurrency(reportData.summary.avgPrice)} />
            </div>
            <ReportDataTable
              columns={['Treatment', 'Category', 'Count', 'Revenue']}
              data={reportData.topTreatments.slice(0, 10).map(t => [
                t.name.length > 30 ? t.name.slice(0, 30) + '...' : t.name,
                t.category,
                formatNumber(t.count),
                formatCurrency(t.revenue),
              ])}
            />
          </>
        )
      }
      case 'patient-analytics': {
        const reportData = data as PatientAnalyticsReportData
        return (
          <>
            <div className="grid gap-3 grid-cols-2 sm:grid-cols-5 mb-6">
              <ReportMetricCard label="Total Patients" value={formatNumber(reportData.summary.totalPatients)} />
              <ReportMetricCard label="New" value={formatNumber(reportData.summary.newPatients)} />
              <ReportMetricCard label="Returning" value={formatNumber(reportData.summary.returningPatients)} />
              <ReportMetricCard label="Retention" value={`${reportData.summary.retentionRate}%`} />
              <ReportMetricCard label="Avg Visits" value={reportData.summary.avgVisits.toFixed(1)} />
            </div>
            <ReportDataTable
              columns={['Patient', 'PNO', 'Visits', 'Total Spent']}
              data={reportData.topPatients.slice(0, 10).map(p => [
                p.name,
                p.pno,
                formatNumber(p.visits),
                formatCurrency(p.totalSpent),
              ])}
            />
          </>
        )
      }
      case 'financial-summary': {
        const reportData = data as FinancialSummaryReportData
        return (
          <>
            <div className="grid gap-3 grid-cols-2 sm:grid-cols-5 mb-6">
              <ReportMetricCard label="Total Revenue" value={formatCurrency(reportData.summary.totalRevenue)} />
              <ReportMetricCard label="Invoices" value={formatNumber(reportData.summary.totalInvoices)} />
              <ReportMetricCard label="Patients" value={formatNumber(reportData.summary.uniquePatients)} />
              <ReportMetricCard label="Avg Daily" value={formatCurrency(reportData.summary.avgDailyRevenue)} />
              <ReportMetricCard label="Avg Transaction" value={formatCurrency(reportData.summary.avgTransaction)} />
            </div>
            <ReportDataTable
              columns={['Category', 'Revenue', 'Share']}
              data={reportData.revenueByCategory.slice(0, 10).map(c => [
                c.category,
                formatCurrency(c.revenue),
                `${c.percentage}%`,
              ])}
            />
          </>
        )
      }
      case 'marketing-insights': {
        const reportData = data as MarketingInsightsReportData
        return (
          <>
            <div className="grid gap-3 grid-cols-2 sm:grid-cols-4 mb-6">
              <ReportMetricCard label="New Patients" value={formatNumber(reportData.summary.totalNewPatients)} />
              <ReportMetricCard label="Returning" value={formatNumber(reportData.summary.totalReturningPatients)} />
              <ReportMetricCard label="Acquisition Rate" value={`${reportData.summary.acquisitionRate}%`} />
              <ReportMetricCard label="Retention Rate" value={`${reportData.summary.retentionRate}%`} />
            </div>
            <div className="text-sm font-medium text-text-dark mb-3">Inactive High-Value Patients</div>
            <ReportDataTable
              columns={['Patient', 'PNO', 'Days Inactive', 'Lifetime Value']}
              data={reportData.inactivePatients.slice(0, 10).map(p => [
                p.name,
                p.pno,
                `${p.daysSince}d`,
                formatCurrency(p.totalSpent),
              ])}
            />
          </>
        )
      }
      default:
        return <div className="text-text-muted">Report preview not available</div>
    }
  }

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: 0.3, duration: 0.3 }}
      className="bg-card rounded-xl p-4 sm:p-6"
    >
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-5 pb-4 border-b border-border">
        <div>
          <h3 className="text-[15px] font-semibold text-text-dark">{template.name}</h3>
          <p className="text-xs text-text-muted mt-1">
            {formatDate(period.startDate)} - {formatDate(period.endDate)}
            {branchId !== 'all' && ` | ${branchId}`}
            <span className="ml-2">
              <Clock className="w-3 h-3 inline-block mr-1" />
              Generated {new Date(generatedAt).toLocaleTimeString('en-MY', { hour: '2-digit', minute: '2-digit' })}
            </span>
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" className="gap-2" onClick={onExportCSV}>
            <FileSpreadsheet className="w-4 h-4" />
            <span className="hidden sm:inline">Export CSV</span>
          </Button>
          <Button variant="outline" size="sm" className="gap-2" onClick={onExportPDF}>
            <Printer className="w-4 h-4" />
            <span className="hidden sm:inline">Print PDF</span>
          </Button>
        </div>
      </div>

      {/* Content */}
      {renderReportContent()}
    </motion.div>
  )
}

// ============================================================================
// Report Metric Card (small)
// ============================================================================
interface ReportMetricCardProps {
  label: string
  value: string
}

function ReportMetricCard({ label, value }: ReportMetricCardProps) {
  return (
    <div className="bg-muted rounded-lg p-3">
      <div className="text-sm sm:text-base font-bold text-text-dark">{value}</div>
      <div className="text-xs text-text-muted">{label}</div>
    </div>
  )
}

// ============================================================================
// Report Data Table
// ============================================================================
interface ReportDataTableProps {
  columns: string[]
  data: string[][]
}

function ReportDataTable({ columns, data }: ReportDataTableProps) {
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead>
          <tr className="border-b border-border">
            {columns.map((col, i) => (
              <th
                key={col}
                className={cn(
                  'py-2 px-2 text-xs font-semibold text-text-muted uppercase tracking-wider',
                  i === 0 ? 'text-left' : 'text-right'
                )}
              >
                {col}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {data.map((row, rowIndex) => (
            <tr key={rowIndex} className="border-b border-border/50 hover:bg-muted/50 transition-colors">
              {row.map((cell, cellIndex) => (
                <td
                  key={cellIndex}
                  className={cn(
                    'py-2 px-2',
                    cellIndex === 0 ? 'text-left font-medium text-text-dark' : 'text-right text-text-muted'
                  )}
                >
                  {cell}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}

// ============================================================================
// Placeholder Tab Component
// ============================================================================
interface PlaceholderTabProps {
  title: string
  description: string
}

function PlaceholderTab({ title, description }: PlaceholderTabProps) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.2 }}
      className="flex flex-col items-center justify-center py-20 text-center"
    >
      <h3 className="text-lg font-semibold text-text-dark mb-2">{title}</h3>
      <p className="text-sm text-text-muted max-w-md">{description}</p>
      <p className="text-xs text-text-muted mt-4 bg-muted px-3 py-1.5 rounded-full">
        Coming Soon
      </p>
    </motion.div>
  )
}

// ============================================================================
// KPI Card Component
// ============================================================================
interface KpiCardProps {
  title: string
  value: string
  growth: number | null
  icon: React.ElementType
  delay?: number
}

function KpiCard({ title, value, growth, icon: Icon, delay = 0 }: KpiCardProps) {
  const isPositive = growth !== null && growth >= 0
  const showGrowth = growth !== null && growth !== 0

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay, duration: 0.3 }}
      className="bg-card rounded-xl p-4 sm:p-5"
    >
      <div className="flex items-start justify-between mb-3">
        <div className="w-10 h-10 sm:w-11 sm:h-11 rounded-full bg-icon-beige flex items-center justify-center">
          <Icon className="w-5 h-5 sm:w-[22px] sm:h-[22px] text-foreground" />
        </div>
        {showGrowth && (
          <div
            className={cn(
              'flex items-center gap-1 px-2 py-1 rounded-full text-xs font-semibold',
              isPositive ? 'bg-success/10 text-success' : 'bg-destructive/10 text-destructive'
            )}
          >
            {isPositive ? (
              <TrendingUp className="w-3 h-3" />
            ) : (
              <TrendingDown className="w-3 h-3" />
            )}
            {Math.abs(growth)}%
          </div>
        )}
      </div>
      <div className="text-xl sm:text-2xl font-bold text-text-dark mb-1">{value}</div>
      <div className="text-xs sm:text-sm text-text-muted">{title}</div>
    </motion.div>
  )
}

// ============================================================================
// Revenue Trend Chart (Line)
// ============================================================================
interface RevenueTrendChartProps {
  data: RevenueTrendItem[]
}

function RevenueTrendChart({ data }: RevenueTrendChartProps) {
  const formatYAxis = (value: number) => {
    if (value >= 1000000) return `${(value / 1000000).toFixed(1)}M`
    if (value >= 1000) return `${(value / 1000).toFixed(0)}K`
    return value.toString()
  }

  const CustomTooltip = ({ active, payload, label }: any) => {
    if (active && payload && payload.length) {
      return (
        <div className="bg-card border border-border rounded-lg px-3 py-2 shadow-lg">
          <p className="text-sm font-medium text-foreground">{label}</p>
          <p className="text-sm text-emerald-500 font-semibold">
            {formatCurrency(payload[0].value)}
          </p>
        </div>
      )
    }
    return null
  }

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: 0.4, duration: 0.3 }}
      className="bg-card rounded-xl p-4 sm:p-6"
    >
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-sm sm:text-[15px] font-semibold text-text-dark">Revenue Trend</h3>
        <Button variant="ghost" size="sm" className="gap-1.5 text-text-muted">
          Monthly
          <ChevronDown className="w-4 h-4" />
        </Button>
      </div>
      <div className="h-[280px]">
        <ResponsiveContainer width="100%" height="100%">
          <LineChart data={data} margin={{ top: 10, right: 10, left: -10, bottom: 0 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="var(--color-border)" />
            <XAxis
              dataKey="label"
              tick={{ fontSize: 11, fill: 'var(--color-text-muted)' }}
              tickLine={false}
              axisLine={false}
            />
            <YAxis
              tickFormatter={formatYAxis}
              tick={{ fontSize: 11, fill: 'var(--color-text-muted)' }}
              tickLine={false}
              axisLine={false}
              width={50}
            />
            <Tooltip content={<CustomTooltip />} />
            <Line
              type="monotone"
              dataKey="revenue"
              stroke="#10b981"
              strokeWidth={2.5}
              dot={{ fill: '#10b981', strokeWidth: 2, r: 4 }}
              activeDot={{ r: 6 }}
            />
          </LineChart>
        </ResponsiveContainer>
      </div>
    </motion.div>
  )
}

// ============================================================================
// Branch Comparison Chart (Bar)
// ============================================================================
interface BranchComparisonChartProps {
  data: BranchComparisonItem[]
}

const BRANCH_COLORS: Record<string, string> = {
  'putrajaya': '#3b82f6', // Blue
  'shah-alam': '#10b981', // Green
  'bangi': '#f59e0b', // Amber
  'kuala-lumpur': '#8b5cf6', // Purple
}

// Short names for display
const BRANCH_SHORT_NAMES: Record<string, string> = {
  'putrajaya': 'ACP',
  'shah-alam': 'ACSA',
  'bangi': 'ACB',
  'kuala-lumpur': 'ACKL',
}

function BranchComparisonChart({ data }: BranchComparisonChartProps) {
  // Add short names to data
  const chartData = data.map((b) => ({
    ...b,
    shortName: BRANCH_SHORT_NAMES[b.code] || b.code.toUpperCase(),
  }))

  const formatYAxis = (value: number) => {
    if (value >= 1000000) return `${(value / 1000000).toFixed(1)}M`
    if (value >= 1000) return `${(value / 1000).toFixed(0)}K`
    return value.toString()
  }

  const CustomTooltip = ({ active, payload, label }: any) => {
    if (active && payload && payload.length) {
      const branch = chartData.find((b) => b.shortName === label)
      return (
        <div className="bg-card border border-border rounded-lg px-3 py-2 shadow-lg">
          <p className="text-sm font-medium text-foreground">{branch?.name || label}</p>
          <p className="text-sm text-primary font-semibold">
            {formatCurrency(payload[0].value)}
          </p>
          <p className="text-xs text-text-muted">
            {branch?.percentage}% of total | {branch?.patients.toLocaleString()} patients
          </p>
        </div>
      )
    }
    return null
  }

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: 0.5, duration: 0.3 }}
      className="bg-card rounded-xl p-4 sm:p-6"
    >
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-sm sm:text-[15px] font-semibold text-text-dark">Branch Comparison</h3>
      </div>
      {/* Legend */}
      <div className="flex flex-wrap gap-3 sm:gap-4 mb-4">
        {chartData.map((branch) => (
          <div key={branch.code} className="flex items-center gap-2 text-xs text-text-muted">
            <span
              className="w-2.5 h-2.5 rounded-full"
              style={{ backgroundColor: BRANCH_COLORS[branch.code] || '#6b7280' }}
            />
            {branch.shortName} ({branch.percentage}%)
          </div>
        ))}
      </div>
      <div className="h-[240px]">
        <ResponsiveContainer width="100%" height="100%">
          <BarChart data={chartData} margin={{ top: 10, right: 10, left: -10, bottom: 0 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="var(--color-border)" />
            <XAxis
              dataKey="shortName"
              tick={{ fontSize: 12, fill: 'var(--color-text-muted)', fontWeight: 500 }}
              tickLine={false}
              axisLine={false}
            />
            <YAxis
              tickFormatter={formatYAxis}
              tick={{ fontSize: 11, fill: 'var(--color-text-muted)' }}
              tickLine={false}
              axisLine={false}
              width={50}
            />
            <Tooltip content={<CustomTooltip />} />
            <Bar dataKey="revenue" radius={[4, 4, 0, 0]}>
              {chartData.map((entry) => (
                <Cell key={entry.code} fill={BRANCH_COLORS[entry.code] || '#6b7280'} />
              ))}
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </div>
    </motion.div>
  )
}

// ============================================================================
// Patient Type Chart (Donut)
// ============================================================================
interface PatientTypeChartProps {
  data: PatientTypeItem[]
}

const PATIENT_TYPE_COLORS = ['#10b981', '#3b82f6', '#f59e0b']

function PatientTypeChart({ data }: PatientTypeChartProps) {
  const total = data.reduce((sum, item) => sum + item.count, 0)

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: 0.6, duration: 0.3 }}
      className="bg-card rounded-xl p-4 sm:p-6"
    >
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-sm sm:text-[15px] font-semibold text-text-dark">New vs Returning</h3>
      </div>
      <div className="flex items-center gap-6">
        <div className="w-[160px] h-[160px] sm:w-[180px] sm:h-[180px]">
          <ResponsiveContainer width="100%" height="100%">
            <PieChart>
              <Pie
                data={data as any[]}
                cx="50%"
                cy="50%"
                innerRadius={50}
                outerRadius={75}
                paddingAngle={2}
                dataKey="count"
                nameKey="type"
              >
                {data.map((_, index) => (
                  <Cell key={`cell-${index}`} fill={PATIENT_TYPE_COLORS[index % PATIENT_TYPE_COLORS.length]} />
                ))}
              </Pie>
              <Tooltip
                formatter={(value, name) => [
                  `${(value as number).toLocaleString()} (${total > 0 ? Math.round(((value as number) / total) * 100) : 0}%)`,
                  name,
                ]}
                contentStyle={{
                  backgroundColor: 'var(--color-card)',
                  border: '1px solid var(--color-border)',
                  borderRadius: '8px',
                }}
              />
            </PieChart>
          </ResponsiveContainer>
        </div>
        {/* Legend */}
        <div className="flex flex-col gap-3">
          {data.map((item, index) => {
            const percentage = total > 0 ? Math.round((item.count / total) * 100) : 0
            return (
              <div key={item.type} className="flex items-center gap-3">
                <span
                  className="w-3 h-3 rounded-full"
                  style={{ backgroundColor: PATIENT_TYPE_COLORS[index % PATIENT_TYPE_COLORS.length] }}
                />
                <div>
                  <div className="text-sm font-medium text-text-dark">
                    {item.type} ({percentage}%)
                  </div>
                  <div className="text-xs text-text-muted">
                    {item.count.toLocaleString()} visits | {formatCurrency(item.revenue)}
                  </div>
                </div>
              </div>
            )
          })}
        </div>
      </div>
    </motion.div>
  )
}

// ============================================================================
// Top Treatments Chart (Horizontal Bar)
// ============================================================================
interface TopTreatmentsChartProps {
  data: TopTreatmentItem[]
}

function TopTreatmentsChart({ data }: TopTreatmentsChartProps) {
  const maxRevenue = Math.max(...data.map((t) => t.revenue))

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: 0.7, duration: 0.3 }}
      className="bg-card rounded-xl p-4 sm:p-6"
    >
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-sm sm:text-[15px] font-semibold text-text-dark">Top Treatments</h3>
        <span className="text-xs text-text-muted">By Revenue</span>
      </div>
      <div className="flex flex-col gap-3">
        {data.slice(0, 5).map((treatment, index) => {
          const percentage = maxRevenue > 0 ? (treatment.revenue / maxRevenue) * 100 : 0
          return (
            <div key={treatment.name} className="flex items-center gap-3">
              <span className="w-5 text-xs font-semibold text-text-muted text-right">
                {index + 1}.
              </span>
              <div className="flex-1">
                <div className="flex items-center justify-between mb-1">
                  <span className="text-sm font-medium text-text-dark truncate max-w-[200px]">
                    {treatment.name}
                  </span>
                  <span className="text-sm font-semibold text-success">
                    {formatCurrency(treatment.revenue)}
                  </span>
                </div>
                <div className="h-2 bg-muted rounded-full overflow-hidden">
                  <motion.div
                    initial={{ width: 0 }}
                    animate={{ width: `${percentage}%` }}
                    transition={{ delay: 0.8 + index * 0.05, duration: 0.4 }}
                    className="h-full bg-gradient-to-r from-emerald-500 to-emerald-400 rounded-full"
                  />
                </div>
                <div className="flex items-center justify-between mt-1">
                  <span className="text-xs text-text-muted">
                    {treatment.category || 'Uncategorized'}
                  </span>
                  <span className="text-xs text-text-muted">
                    {treatment.count.toLocaleString()} treatments
                  </span>
                </div>
              </div>
            </div>
          )
        })}
      </div>
    </motion.div>
  )
}

// ============================================================================
// Utility Functions
// ============================================================================
function formatCurrency(value: number): string {
  if (value >= 1000000) return `RM ${(value / 1000000).toFixed(2)}M`
  if (value >= 1000) return `RM ${(value / 1000).toFixed(0)}K`
  return `RM ${value.toLocaleString()}`
}

function formatNumber(value: number): string {
  return value.toLocaleString()
}

function formatDate(dateStr: string): string {
  const date = new Date(dateStr)
  return date.toLocaleDateString('en-MY', { day: 'numeric', month: 'short', year: 'numeric' })
}
